/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * What a form tells a person after they press Submit.
 *
 * Kept apart from FormBuilderSection because these are decisions about
 * meaning — did this fail, why, and where should the person look — and they
 * are worth reading and testing without a rendering tree around them.
 */

/**
 * Reads a failed submit response into the three things a person can be shown.
 *
 * These used to be conflated, which is why a failure could look like nothing
 * happening at all:
 *
 *  - the server wants a sign-in     → send them there, come back afterwards
 *  - it rejected particular fields  → show each against its own field
 *  - anything else                  → one message at the top of the form
 *
 * TWO BUGS THIS FIXES.
 *
 * `body.error` is now read as well as `body.detail`. The bound-form path
 * reports under `error`, and only `detail` was looked at, so those failures
 * arrived as an empty banner.
 *
 * An empty `errors` object no longer counts as field errors. `{}` is truthy,
 * so it used to be applied as "the errors" — clearing nothing, showing nothing,
 * and leaving the form silent.
 */
export function readSubmitFailure(body) {
  const payload = body || {};
  const fieldErrors = payload.errors && Object.keys(payload.errors).length
    ? payload.errors
    : null;
  return {
    requiresAuth: payload.requires_auth === true,
    fieldErrors,
    message: fieldErrors
      ? ''
      : (payload.error || payload.detail || 'Submission failed. Please try again.'),
  };
}

/**
 * The index of the first stage holding any rejected field, or -1.
 *
 * THE BUG THIS EXISTS FOR: a staged form renders only the current stage, so an
 * error against a field on an earlier stage was set and then never displayed.
 * Pressing Submit appeared to do nothing at all — no success, no error.
 *
 * `stageFieldIds` is one array of field ids per stage, so this module stays
 * free of any knowledge of how a stage is shaped.
 */
export function stageHoldingFirstError(stageFieldIds, fieldErrors) {
  const ids = Object.keys(fieldErrors || {});
  if (!ids.length) return -1;
  for (let i = 0; i < (stageFieldIds || []).length; i += 1) {
    const inStage = stageFieldIds[i] || [];
    if (ids.some(id => inStage.includes(id))) return i;
  }
  return -1;
}

/**
 * Substitutes the ids a submission just created into a destination URL, so a
 * form can send someone to the record they made: `/events/@e.id` becomes
 * `/events/412`.
 *
 * A name with no matching ref is left exactly as it was. Replacing it with
 * "undefined" would produce a URL that looks real and goes nowhere.
 */
export function fillCreatedIds(url, refs) {
  return String(url).replace(
    /@([A-Za-z0-9_]+)(?:\.id)?/g,
    (whole, name) => (refs && refs[name] !== undefined ? String(refs[name]) : whole),
  );
}

/**
 * Where to send someone after a successful submit, or null to stay put.
 *
 * Separated from the navigating itself so the decision can be tested without a
 * browser. `message` is the default, so a form with nothing configured behaves
 * exactly as it always has.
 */
export function successDestination(cfg, body) {
  const onSuccess = (cfg || {}).on_success || {};
  const mode = onSuccess.mode || 'message';
  if (mode === 'message' || mode === 'message_then_button' || !onSuccess.url) return null;
  return {
    url: fillCreatedIds(onSuccess.url, body && body.refs),
    delayMs: mode === 'message_then_navigate'
      ? (onSuccess.delay_ms === undefined ? 1500 : onSuccess.delay_ms)
      : 0,
  };
}

/**
 * The button to show under the success message, or null for none.
 *
 * `message_then_button` leaves the person on the message and lets them choose
 * when to move on, which is why successDestination never navigates for it.
 * Without a destination there is nothing to link to, so no button is shown.
 */
export function successButton(cfg, body) {
  const onSuccess = (cfg || {}).on_success || {};
  if (onSuccess.mode !== 'message_then_button' || !onSuccess.url) return null;
  return {
    url: fillCreatedIds(onSuccess.url, body && body.refs),
    label: onSuccess.button_label || 'Continue',
  };
}

/**
 * The values with every read-only field set to its fixed `defaultValue`.
 *
 * A read-only field is not something the person answers, so its value comes
 * from the form, not from typing — and it has to hold after a saved draft is
 * restored or the form is reset, which is why this runs on every change rather
 * than once at load. Returns the same object when nothing needed fixing, so a
 * state update with it is a no-op.
 */
export function withFixedValues(fields, values) {
  const current = values || {};
  let next = current;
  (fields || []).forEach(field => {
    if (!field || field.readOnly !== true) return;
    const fixed = field.defaultValue;
    if (fixed === undefined || fixed === null || fixed === '') return;
    if (current[field.id] === fixed) return;
    if (next === current) next = { ...current };
    next[field.id] = fixed;
  });
  return next;
}

/**
 * True when a thrown error means "your session is over", rather than a genuine
 * network failure.
 *
 * THE BUG THIS FIXES: the API client throws for an expired session or a dead
 * token instead of returning a response, so those landed in the same catch as a
 * dropped connection and were reported as "Network error. Please try again." —
 * advice that cannot work, for a problem signing in again solves in one click.
 */
export function isSessionGone(err) {
  const type = err && (err.type || (err.cause && err.cause.type));
  if (type === 'session_expired' || type === 'oauth_token_invalid') return true;
  return !!(err && typeof err.message === 'string'
            && err.message.includes('Failed to obtain access token'));
}

/**
 * The next value of a location field when one half of the pair is edited.
 *
 * A geo column stores a point, so it wants BOTH numbers or neither. A latitude
 * with no longitude is not a place, and sending one would either be refused by
 * the column or — worse — stored as a point somewhere it has never been.
 *
 * Returns '' once the pair is empty, so a required location still reads as
 * unanswered rather than as an object that happens to be truthy.
 */
export function geoPointValue(current, part, raw) {
  const next = (current && typeof current === 'object') ? { ...current } : {};
  if (raw === '' || raw === null || raw === undefined) delete next[part];
  else next[part] = Number(raw);

  const has = (k) => typeof next[k] === 'number' && !Number.isNaN(next[k]);
  if (has('lat') && has('lng')) return next;
  return Object.keys(next).length ? next : '';
}

/** True when a location field holds a usable point. */
export function isCompletePoint(value) {
  return !!value && typeof value === 'object'
    && typeof value.lat === 'number' && !Number.isNaN(value.lat)
    && typeof value.lng === 'number' && !Number.isNaN(value.lng);
}

// ─── Repeated blocks ─────────────────────────────────────────────────────────

/**
 * The blocks of questions the person may add more of, read from the form's own
 * table bindings.
 *
 * WHICH FIELDS REPEAT IS NOT SEPARATE CONFIGURATION. An operation marked
 * `repeat` already says which answers it saves, so those are exactly the ones
 * that repeat — the two cannot disagree, because there is only one of them.
 */
export function repeatGroups(cfg) {
  return (((cfg || {}).operations) || [])
    .filter(op => op && op.repeat)
    .map(op => ({
      key: op.ref || op.table,
      table: op.table,
      label: op.repeat_label || '',
      fieldIds: Object.values(op.visitor_fields || {})
        .filter(v => typeof v === 'string' && v.startsWith('@form.'))
        .map(v => v.slice('@form.'.length)),
    }))
    .filter(group => group.fieldIds.length > 0);
}

/** Every field id that belongs to some repeated block. */
export function repeatedFieldIds(cfg) {
  const ids = new Set();
  repeatGroups(cfg).forEach(g => g.fieldIds.forEach(id => ids.add(id)));
  return ids;
}

/**
 * How many copies to show. At least one, so an empty repeated block still
 * presents a first set of questions rather than nothing at all.
 */
export function copyCount(group, values) {
  let n = 0;
  group.fieldIds.forEach(id => {
    const v = (values || {})[id];
    if (Array.isArray(v)) n = Math.max(n, v.length);
  });
  return Math.max(n, 1);
}

/** Sets one answer inside one copy, leaving the other copies alone. */
export function setCopyValue(values, fieldId, index, value) {
  const existing = Array.isArray((values || {})[fieldId]) ? [...values[fieldId]] : [];
  while (existing.length <= index) existing.push('');
  existing[index] = value;
  return { ...(values || {}), [fieldId]: existing };
}

/** Drops one copy from every field of a block, keeping the rest in order. */
export function removeCopy(values, group, index) {
  const next = { ...(values || {}) };
  group.fieldIds.forEach(id => {
    if (Array.isArray(next[id])) {
      const arr = [...next[id]];
      arr.splice(index, 1);
      next[id] = arr;
    }
  });
  return next;
}
